"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BACKGROUNDS,
  DEFAULT_BG,
  DEFAULT_DARK_BG,
  DEFAULT_ORGANISM,
  MINTED_BG_ID,
  applyGroundVars,
  applyOrganismVars,
  groundRootCss,
  harmonizedGrounds,
  paintThemeColor,
  parseGroundRow,
  persistGround,
  persistOrganism,
  readNewbornGround,
  readStoredGround,
  wearsOwnGround,
  type Background,
  type ColorState,
  type GroundRow,
  type RGB,
} from "../lib/theme";
import { createClient } from "../lib/supabase/client";
import { DASHBOARD_HOST } from "../lib/subdomain";

type ThemeContextValue = {
  /** Current ground (user-customizable). */
  bg: Background;
  /** RGB (0..1) of the ground — fed to the organism so it reacts/glows. */
  bgRgb: RGB;
  /** The full bg catalogue for the settings picker. */
  backgrounds: Background[];
  /** Grounds generated from the active organism, in unison with its colour. */
  harmonizedGrounds: Background[];
  /** Pick a catalogue ground, or set a custom one. */
  setBackground: (bg: Background) => void;
  /** Organism palette state — personality/identity-driven, not picked here. */
  organism: ColorState;
  /** Seed the organism (called from signed-in surfaces with the user's identity
   *  palette). The single authority for --organism-*; repaints + persists. */
  setOrganism: (o: ColorState) => void;
  /** Take the ground the SERVER rendered for this person (GroundSeed). It
   *  outranks this device's cache and the account fetch, which can only be as
   *  fresh as it. `keep: false` paints without saving (the mirror's demo). */
  adoptGround: (ground: GroundRow, keep?: boolean) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** the same ground: the same id and, within rounding, the same colour (the
 *  account hands back the pick it was sent, through a json round trip). */
function sameGround(a: Background, b: Background): boolean {
  return a.id === b.id && a.rgb.every((c, i) => Math.abs(c - b.rgb[i]) < 1e-6);
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [organism, setOrganismState] = useState<ColorState>(DEFAULT_ORGANISM);
  const [bg, setBg] = useState<Background>(DEFAULT_BG);
  // browser Supabase client (RLS-gated) — lets the chosen ground follow the
  // account across devices. Null on a surface without env; every call below is
  // guarded so a signed-out state / missing table simply falls back to local.
  const supabase = useMemo(() => {
    try { return createClient(); } catch { return null; }
  }, []);
  // a ground the server already handed over (GroundSeed). Once set, nothing
  // weaker (this device's cache, a guess, the account fetch) repaints over it.
  const seeded = useRef<Background | null>(null);
  // the ground the person picked in THIS document, if they did. It is newer
  // than any server render this document can still meet: Back reuses the
  // dashboard's render from before the pick, and a refresh can come back
  // before the pick's upsert has landed. Either one remounts GroundSeed with
  // the older ground, and adopting it undid the pick and saved the old colour
  // over it on this device. A new document starts with no pick and takes what
  // the server renders.
  const picked = useRef<Background | null>(null);

  // paint YOUR ground: the vars, the one gradient, and the browser's own chrome.
  const paint = useCallback((rgb: RGB) => {
    applyGroundVars(rgb);
    paintThemeColor(rgb);
  }, []);

  // Hydrate the persisted ground once on mount, then keep vars in sync. A custom
  // ground is only honored on the dashboard surfaces — public/auth pages stay on
  // the egg-white default ground for everyone, logged in or not.
  useEffect(() => {
    // one list with the boot script (lib/theme OWN_GROUND_PATHS) — `/ours` is
    // added here only so leaving a room restores YOUR ground (see onDocs).
    const onApp =
      wearsOwnGround(window.location.pathname, window.location.hostname, DASHBOARD_HOST) ||
      /^\/ours(\/|$)/.test(window.location.pathname);
    // /docs owns its own permanent dark grey-blue ground (DocsOsShell applies it
    // via a layout effect that runs BEFORE this one, since it's a child mounting
    // deeper in the tree with useLayoutEffect vs. this plain useEffect). Never
    // apply this provider's ground here, or it clobbers the docs ground back to
    // the egg-white default once this effect fires after paint. /ours and /share
    // are the same shape: the SPACE's (or the SHARER's) ground owns those pages
    // — the provider still hydrates `bg` state (so leaving restores YOUR ground)
    // but never repaints over the room's colour. /kit is the media kit's stage:
    // its ground is whatever the url asked for (lib/kit/stage.ts). The investor
    // deck tweens its own rooms (DeckClient) and boots on its cover's blue: a
    // repaint here turned the fading boot veil egg-white for a frame.
    const onDocs = /^\/(docs|ours|share|kit|investors\/deck)(\/|$)/.test(window.location.pathname);

    // Gate the body's background/color transition behind `.theme-ready` so the
    // FIRST paint (persisted, system-dark, or egg-white — whichever `initial`
    // resolved to) never eases in from a temporary default. Enabled on the next
    // frame, once this synchronous ground application has already landed.
    requestAnimationFrame(() => document.documentElement.classList.add("theme-ready"));
    // GROUND FEEL — the netti space's radial field over the base ground (a page-
    // level veil; --bg itself stays exact for the organism). App surfaces only.
    if (onApp) {
      try {
        const feel = localStorage.getItem("innernet:ground-feel");
        if (feel && feel !== "still") {
          void import("./os/NettiSpace").then((m) => m.applyGroundFeel(feel));
        }
      } catch { /* private mode */ }
    }

    // the server rendered this person's ground into the page and GroundSeed
    // already took it (children's effects run first): it is the account's own
    // answer, read moments ago, so there is nothing left to resolve or fetch.
    if (seeded.current) return;

    // What do we KNOW, in order: the ground this device saved (now with its
    // exact rgb, so a dial pick comes back as itself, not as cream), then a
    // newborn's ground riding the birth's cookie onto a host that has never
    // seen them. Anything else is a default.
    let initial: Background = DEFAULT_BG;
    let known = false;
    if (onApp) {
      const stored = readStoredGround();
      const newborn = stored ? null : readNewbornGround();
      if (stored) { initial = stored; known = true; }
      else if (newborn) {
        initial = { id: MINTED_BG_ID, label: "yours", rgb: newborn };
        known = true;
        // this origin keeps it from now on (the cookie is cleared on adoption)
        persistGround(initial.id, initial.rgb);
      }
      // First-time visitor, nothing known: honor the OS's dark preference
      // instead of always falling through to the egg-white default (the boot
      // script already painted this same value in a <style> — this keeps
      // React's state in sync with it).
      else if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) initial = DEFAULT_DARK_BG;
    }
    setBg(initial);
    // Apply the GROUND only on mount — never the organism, so a returning user's
    // no-flash-restored colour (or the public default) is never clobbered by a
    // generic default before OrganismIdentity seeds the real one. Skipped on
    // /docs — DocsOsShell already applied its own permanent dark ground and
    // owns that surface; this provider's `bg` state still tracks DEFAULT_BG
    // for context consumers, it just never repaints --bg over the docs ground.
    //
    // On your own surfaces a DEFAULT is never painted inline: the boot script
    // already shows it (cream from the stylesheet, slate from its <style>), and
    // an inline guess would outrank the account's ground the dashboard is about
    // to stream in as a stylesheet rule. A guess must lose to the truth.
    if (!onDocs && (known || !onApp)) {
      if (onApp) paint(initial.rgb);
      else applyGroundVars(initial.rgb);
    }

    // cross-device: if signed in, the account's saved ground wins over the local
    // cache (so a fresh device picks up your choice). Graceful — a missing row /
    // table / signed-out state leaves the local ground in place.
    if (onApp && supabase) {
      (async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;
          const { data } = await supabase.from("user_settings").select("ground").eq("user_id", user.id).maybeSingle();
          const g = parseGroundRow(data?.ground ?? null);
          // the server's own render landed meanwhile: it is at least as fresh.
          if (!g || seeded.current) return;
          const next: Background = { id: g.bgId, label: g.bgId, rgb: g.rgb };
          setBg(next);
          // and this device remembers it, so the next load paints it at once
          persistGround(next.id, next.rgb);
          // on a surface that paints its OWN ground (/ours, /share, /docs) only
          // the state updates — the room's colour stays on the page.
          if (!onDocs) paint(next.rgb);
        } catch { /* table absent / offline — the local ground stands */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adoptGround = useCallback(
    (ground: GroundRow, keep = true) => {
      const next: Background = { id: ground.bgId, label: ground.bgId, rgb: ground.rgb };
      // a pick in this document outranks the server's render of an older one.
      // The pick is repainted rather than merely kept: the page in between may
      // have painted its own ground (a share puts cream back as it leaves).
      // The mirror's pretend person (`keep: false`) still wears its own.
      const mine = picked.current;
      if (keep && mine && !sameGround(mine, next)) {
        seeded.current = mine;
        setBg(mine);
        paint(mine.rgb);
        return;
      }
      seeded.current = next;
      setBg(next);
      paint(next.rgb);
      if (keep) persistGround(next.id, next.rgb);
    },
    [paint],
  );

  const setBackground = useCallback(
    (next: Background) => {
      picked.current = next;
      setBg(next);
      paint(next.rgb);
      persistGround(next.id, next.rgb);
      // a pick is newer than anything the server rendered
      if (seeded.current) seeded.current = next;
      // ...and save to the account so the choice follows you across devices.
      if (supabase) {
        (async () => {
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            await supabase.from("user_settings").upsert({ user_id: user.id, ground: { bgId: next.id, rgb: next.rgb }, updated_at: new Date().toISOString() });
          } catch { /* ignore — the local persist already landed */ }
        })();
      }
    },
    [paint, supabase],
  );

  // The organism palette authority — the ONLY writer of --organism-* on
  // signed-in surfaces. Applies instantly (called from OrganismIdentity's layout
  // effect, so it lands pre-paint) and persists so the boot script repaints the
  // whole UI on the next load with zero flash. It saves the ORGANISM only: it
  // used to save the ground React held alongside it, and from a layout effect
  // that runs before this provider's mount that ground was still the cream
  // placeholder, written over the colour the boot script had just restored.
  const setOrganism = useCallback((next: ColorState) => {
    setOrganismState(next);
    applyOrganismVars(next);
    persistOrganism(next);
  }, []);

  const grounds = useMemo(() => harmonizedGrounds(organism), [organism]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      bg,
      bgRgb: bg.rgb,
      backgrounds: BACKGROUNDS,
      harmonizedGrounds: grounds,
      setBackground,
      organism,
      setOrganism,
      adoptGround,
    }),
    [bg, grounds, setBackground, organism, setOrganism, adoptGround],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

// The ground the server already holds for this person, IN the HTML: a stylesheet
// rule with exactly what applyGroundVars would write (lib/theme groundRootCss),
// rendered as the dashboard's first child. So a new device, the apex → yours
// hop, a cleared cache: all of them open on the account's own colour before any
// script has run, and the boot script's guesses (a newborn cookie, the dark
// default) lose to it, because a later rule beats an earlier one. On hydration
// the provider adopts it as the truth (state, inline paint, this device's
// cache). `demo` paints it and keeps nothing, for the mirror's pretend person.
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function GroundSeed({ ground }: { ground: (GroundRow & { demo?: boolean }) | null | undefined }) {
  const theme = useContext(ThemeContext);
  const row = useMemo(() => parseGroundRow(ground), [ground]);
  const css = useMemo(() => (row ? groundRootCss(row.rgb) : null), [row]);
  const adopt = theme?.adoptGround;
  const keep = !ground?.demo;
  // adopt once per ground (the provider's callbacks are stable), keyed by value
  // so a re-render handing over the same colour is not a second adoption
  const key = row ? `${row.bgId}:${row.rgb.join(",")}:${keep ? 1 : 0}` : "";
  useIsoLayoutEffect(() => {
    if (row && adopt) adopt(row, keep);
  }, [key]);
  if (!css) return null;
  return <style data-ground-seed="" dangerouslySetInnerHTML={{ __html: css }} />;
}
